import TransactionCard from '../transactionCard';
import { useFetchMyLastSevenDaysHourTransactions } from '../../../hooks/useFetchMyLastSevenDaysHourTransactions';
import { IHourTransaction, ITransaction } from '../../../interface/transaction';
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  formatDateGroupTitle,
  formatGroupHours,
  formatHourTransactionsDate,
  formatter,
} from '../../../constants/utilityFunctions';
import { useTranslation } from 'react-i18next';
import { useFetchDashboardTransactions } from '../../../hooks/useFetchTransactions';
import dayjs from 'dayjs';
import { useUser } from '../../../context/UserContext';
import { ROUTES, UserType } from '../../../constants/constants';
import NoData from '../noData';
import { useLanguage } from '../../../context/LanguageContext';
import { useQueryClient } from '@tanstack/react-query';
import SearchInputWithFilters from '../SearchInputWithFilters';
import { TransactionFilters, TransactionStatus } from '../../../constants/transaction';
import RenderTransactionRestrictions from '../addTransaction/renderTransactionRestrictions';
import { Divider, Paragraph, Spin, Switch, Text } from '../../Ant';
import { Flex } from 'antd';
import { CustomLegend } from '../../../assets/styles/components/dashboard.styled';
import RecentGraph from '../graphs/recentTransactionGraphs';
import CollapsibleChartPanel from '../../collapsibleChartPanel/collapsibleChartPanel';
import { formatChartDateRange } from '../graphs/chartPanelSummary';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useSearchParams } from 'react-router-dom';
import { useTransaction } from '../../../context/TransactionContext';
import { useCopyTransaction } from '../../../hooks/useCopyTransaction';
import { IActiveSortState, ISortOption } from '../../../interface/common';
import SortSelector from '../../sortPopover/sortSelector';
import {
  fillCurrentWeekHours,
  filterCurrentWeek,
  filterLast7DaysWithWeekends,
  getCurrentWeekDays,
} from '../../../helpers/timesheetHelper';
import SkeletonTransactionList from '../../common/skeleton/skeletonTransactionList';
import SkeletonChart from '../../common/skeleton/skeletonChart';
import CollapsibleListLayout from '../../collapsibleListLayout/collapsibleListLayout';
import { DocumentTextEmpty } from '../../icons';
import { useDashboardChartVisibility } from '../../../hooks/useDashboardChartVisibility';
import CollapsibleDateGroup from '../../collapsibleDateGroup/collapsibleDateGroup';
import {
  DateGroupList,
  toDateGroupKey,
  useCollapsedDateGroups,
} from '../../../hooks/useCollapsedDateGroups';
import { useFetchAllPages } from '../../../hooks/useFetchAllPages';

interface IDayTransactionGroup {
  key: string;
  date: string;
  totalBillable: number;
  transactions: ITransaction[];
}

const RecentTransactions = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { user } = useUser();
  const { isChartVisible, toggleChartVisibility, isLastSevenDays, setLastSevenDays } =
    useDashboardChartVisibility();
  // The switch follows isLastSevenDays at once; the chart and list follow the deferred copy so
  // re-rendering them for the new period cannot hold back the switch's paint.
  const isCurrentWeek = !useDeferredValue(isLastSevenDays);
  const queryClient = useQueryClient();
  const { push } = useNavigationStack();
  const { updateTransaction } = useTransaction();
  const hidePricingInfo =
    user?.userRole?.hidePricingInformation === true || user?.userType === UserType.RESTRICTED;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const transactionFilters = [
    { label: t('transaction:filter.allTransactions'), value: TransactionFilters.all },
    { label: t('transaction:filter.openTransactions'), value: TransactionFilters.open },
  ];
  const initialFilterValue = searchParams.get('filter');
  const initialFilter = transactionFilters.find((opt) => opt.value === initialFilterValue);
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [selectedFilter, setSelectedFilter] = useState<{ label: string; value: string }>(
    initialFilter ?? {
      label: t('transaction:filter.allTransactions'),
      value: TransactionFilters.all,
    },
  );
  const [formattedHourTransactions, setFormattedHourTransactions] = useState<IHourTransaction[]>(
    [],
  );
  const { data: hourTransactions, isLoading: isLoadingHourTransactions } =
    useFetchMyLastSevenDaysHourTransactions(isCurrentWeek);
  const chartHourTransactions = useMemo(
    () => (isCurrentWeek ? fillCurrentWeekHours(hourTransactions) : hourTransactions),
    [hourTransactions, isCurrentWeek],
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const [copyClicked, setCopyClicked] = useState<boolean>();
  const { copyTransaction, startTransactionOnSameMatter } = useCopyTransaction({
    setCopyClicked,
    setSelectedTransaction,
  });
  const { startDate, endDate } = useMemo(() => {
    if (isCurrentWeek) {
      const weekDays = getCurrentWeekDays();
      return { startDate: weekDays[0], endDate: weekDays[weekDays.length - 1] };
    }
    return {
      startDate: dayjs().subtract(9, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    };
  }, [isCurrentWeek]);
  const initialClickedDate = searchParams.get('date');
  const [clickedDate, setClickedDate] = useState<string | null>(initialClickedDate ?? null);
  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'transactionDate',
    direction: 'DESC',
  });
  const {
    data: transactionList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refetch,
    isLoading: isLoadingRecentTransactions,
    isFetching,
  } = useFetchDashboardTransactions({
    startDate,
    endDate,
    clickedDate,
    pageSize: 50,
    search: searchValue,
    relations: [
      'matter:matterNumber,subject,currency,invoiceableType,language',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
      'unit:translations',
      'transactionType',
    ],
    extraFilters: {
      transactionUserId: user?.id ?? '',
      ...(selectedFilter.value.includes(TransactionFilters.open)
        ? { status: TransactionStatus.open }
        : {}),
    },
    fields: [
      'billableQuantity',
      'totalPrice',
      'totalPriceInCurrency',
      'unitId',
      'unitPrice',
      'unitPriceInCurrency',
      'status',
      'subject',
      'transactionDate',
      'matterId',
      'workedHours',
      'hasImportantNotes',
      'hasNotes',
      'createdOn',
    ],
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    enabled: !!user?.id,
  });
  useFetchAllPages({ hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage });
  const transactionSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'transactionDate',
        label: t('transaction:transactionDate'),
        type: 'date',
        defaultDirection: 'DESC',
      },
    ],
    [t],
  );
  const { isCollapsed, toggle, expand, getToggleAllControl } = useCollapsedDateGroups(
    DateGroupList.recentTransactions,
  );
  const handleClickedDate = useCallback(
    (date: string) => {
      setClickedDate(date);
      if (date) expand(toDateGroupKey(date));
    },
    [expand],
  );
  useEffect(() => {
    setSearchParams({
      search: searchValue,
      filter: selectedFilter?.value || '',
      date: clickedDate || '',
    });
  }, [searchValue, selectedFilter, clickedDate]);
  useEffect(() => {
    if (chartHourTransactions.length > 0) {
      const sortedTransactions: IHourTransaction[] = [...chartHourTransactions].sort(
        (a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime(),
      );

      const formattedTransactions: IHourTransaction[] = sortedTransactions.map(
        (hour: IHourTransaction) => ({
          formatedDate: formatHourTransactionsDate(hour.transactionDate, t),
          transactionDate: hour.transactionDate,
          billableHours: hour.billableHours,
        }),
      );
      setFormattedHourTransactions(formattedTransactions);

      // Set selectedDate for initial clicked date from URL params
      if (initialClickedDate && !selectedDate && formattedTransactions.length > 0) {
        const matchingTransaction = formattedTransactions.find(
          (t) => t.transactionDate === initialClickedDate,
        );
        if (matchingTransaction) {
          setSelectedDate(matchingTransaction.formatedDate as string);
        }
      }
    }
  }, [chartHourTransactions, language, initialClickedDate]);
  useEffect(() => {
    if (isChartVisible) return;
    setSelectedDate(null);
    setClickedDate(null);
  }, [isChartVisible]);
  const chartPeriodLabel = t(
    isLastSevenDays ? 'common:chart.lastSevenDays' : 'common:chart.currentWeek',
  );
  const handleChartPeriodChange = (lastSevenDays: boolean) => {
    setLastSevenDays(lastSevenDays);
    setSelectedDate(null);
    setClickedDate(null);
  };

  const totalBillableHours = useMemo((): number => {
    return (
      chartHourTransactions.reduce(
        (sum: number, transaction: IHourTransaction) => sum + transaction.billableHours,
        0,
      ) ?? 0
    );
  }, [chartHourTransactions]);

  const chartDateRange = useMemo(
    () => formatChartDateRange(formattedHourTransactions),
    [formattedHourTransactions],
  );

  const groupedTransactions = useMemo((): IDayTransactionGroup[] => {
    if (transactionList && transactionList.length > 0) {
      const periodTransactions = isCurrentWeek
        ? filterCurrentWeek(transactionList)
        : filterLast7DaysWithWeekends(transactionList, hourTransactions?.data);
      const sortedTransactions =
        activeSort.direction === 'ASC'
          ? [...periodTransactions].sort((a, b) => a.date.localeCompare(b.date))
          : periodTransactions;

      const billableHoursData = hourTransactions ?? [];
      const billableHoursMap = billableHoursData.reduce(
        (acc: Record<string, number>, entry: IHourTransaction) => {
          acc[entry.transactionDate] = entry.billableHours;
          return acc;
        },
        {},
      );

      const grouped = sortedTransactions.reduce(
        (acc, transaction) => {
          const key = toDateGroupKey(transaction?.date);
          acc[key] ??= {
            key,
            date: transaction?.date,
            totalBillable: billableHoursMap[transaction.date] ?? 0,
            transactions: [],
          };
          acc[key].transactions.push(...(transaction?.transactions ?? []));
          return acc;
        },
        {} as Record<string, IDayTransactionGroup>,
      );
      return Object.values(grouped);
    }
    return [];
  }, [transactionList, hourTransactions, activeSort, isCurrentWeek]);
  const handleAdd = () => {
    push(ROUTES.ADD_EDIT_TRANSACTION);
  };
  const handleSelectTransaction = (transaction?: ITransaction) => {
    if (transaction && !copyClicked) {
      push(`${ROUTES.ADD_EDIT_TRANSACTION}/${transaction.id}`);
    } else if (transaction && copyClicked) {
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      updateTransaction(null);
      setCopyClicked(false);
      setSelectedTransaction(undefined);
    }
  };
  const handleCopyTransaction = (transaction: ITransaction) => {
    copyTransaction(transaction);
  };
  const renderLoader = () => {
    if (isFetching) {
      return <Spin />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="recent-transactions-listing"
      contentGap="gutter"
      contentClassName="items-center w-full"
      header={
        <SearchInputWithFilters
          dateGroupToggle={getToggleAllControl(groupedTransactions.map((group) => group.key))}
          handleAdd={handleAdd}
          focusAddOnEntry
          tooltipTextForAdd={t('transaction:addTransaction')}
          searchTitle={t('transaction:search')}
          searchValue={searchValue}
          filterTitle={t('transaction:filter.label')}
          data-testid="top-bar"
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          handleRefresh={() => {
            queryClient.removeQueries({ queryKey: ['transactions'] });
            return refetch();
          }}
          radioOptions={transactionFilters}
          selectedFilter={[selectedFilter]}
          setSelectedFilter={(val) => setSelectedFilter(val)}
          clearFilter={() => {
            setSelectedFilter({
              label: t('transaction:filter.allTransactions'),
              value: TransactionFilters.all,
            });
          }}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={transactionSortOptions}
            activeSort={activeSort}
            onApply={(sort) => setActiveSort(sort)}
          />
        </SearchInputWithFilters>
      }
    >
      <CollapsibleChartPanel
        title={t('transaction:billableHours')}
        subtitle={chartDateRange}
        summary={
          isLoadingHourTransactions
            ? undefined
            : `${formatter.regionalNumberFormat({ value: totalBillableHours })} ${t('common:h')}`
        }
        expanded={isChartVisible}
        onToggle={toggleChartVisibility}
        testId="recent-transactions-chart-panel"
      >
        <Flex justify="flex-end" align="center" gap={8} className="mb-10">
          <Text $fontsize="11px" $lineheight="100%" $color="subtitle">
            {chartPeriodLabel}
          </Text>
          <Switch
            data-testid="recent-transactions-chart-period-switch"
            aria-label={chartPeriodLabel}
            checked={isLastSevenDays}
            onChange={handleChartPeriodChange}
          />
        </Flex>
        {isLoadingHourTransactions ? (
          <SkeletonChart />
        ) : (
          <>
            <RecentGraph
              formattedHourTransactions={formattedHourTransactions}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              handleClickedDate={handleClickedDate}
            />

            <Divider className="mb-8" />
          </>
        )}
        {/* Stays mounted while the skeleton shows: a live region inserted already filled is not announced */}
        <div data-testid="billable-hours-status" role="status" aria-live="polite">
          {!isLoadingHourTransactions && (
            <CustomLegend justify="center" align="center" gap={4}>
              <span className="primary-bar-color"></span>
              <Paragraph
                data-testid="billable-hours"
                $fontsize="12px"
                $color="description"
                $lineheight="18px"
              >
                {t('transaction:billableHours')}:{' '}
                <span data-testid="total-billable-hours" className="legend-data">
                  {formatter.regionalNumberFormat({
                    value: totalBillableHours,
                    minimumFractionDigits: 2,
                  })}{' '}
                  {t('common:h')}
                </span>
              </Paragraph>
            </CustomLegend>
          )}
        </div>
      </CollapsibleChartPanel>
      {groupedTransactions && Object.entries(groupedTransactions).length > 0 && (
        <Flex vertical className="w-full gap-gutter">
          {groupedTransactions.map((group) => (
            <CollapsibleDateGroup
              key={group.key}
              title={formatDateGroupTitle(group.date, t)}
              itemCount={t('transaction:transactionCount', { count: group.transactions.length })}
              total={formatGroupHours(group.totalBillable, t)}
              collapsed={isCollapsed(group.key)}
              onToggle={() => toggle(group.key)}
            >
              {group.transactions.map((transaction) => (
                <TransactionCard
                  data-testid="transaction-card"
                  key={transaction.id}
                  transaction={transaction}
                  hidden={hidePricingInfo}
                  onEdit={(transaction) => {
                    setSelectedTransaction(transaction);
                  }}
                  onCopy={(transaction) => {
                    handleCopyTransaction(transaction);
                  }}
                  onNewToSameMatter={startTransactionOnSameMatter}
                />
              ))}
            </CollapsibleDateGroup>
          ))}
        </Flex>
      )}
      {groupedTransactions.length === 0 && !isLoadingRecentTransactions && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData
            header={t('common:noData')}
            subHeader={t('transaction:noDataContent')}
            icon={<DocumentTextEmpty />}
          />
        </div>
      )}
      {isLoadingRecentTransactions ? <SkeletonTransactionList /> : renderLoader()}
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleSelectTransaction}
      />
    </CollapsibleListLayout>
  );
};

export default RecentTransactions;
